/**
 * useAiChatContext
 *
 * Computes the reactive list of context chips shown above the AI chat input
 * by merging:
 *   - current route path (e.g. /database/atlas-intel/costs)
 *   - atlas-intel global filters (filterClusters, filterProjectIds)
 *   - dateRange (from URL query or atlas-intel timeRange)
 *   - hoveredMetric (set via KpiCard mouse hover)
 *   - selectedRec   (set when user clicks a recommendation in W3's panel)
 *
 * Returned chips are written through to the AI panel store (setChips) so the
 * existing context-injection contract — every send picks up the latest chips —
 * works without each surface re-deriving the same data.
 *
 * The composable also returns `clearChip(id)` which mutes a chip so the user
 * can selectively drop noisy context.
 */
import { computed, watch } from 'vue'
import type { ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import { useAtlasIntelStore } from '../stores/atlas-intel.store'
import { useAiChatPanelStore } from '../stores/ai-chat-panel.store'
import type { ContextChip } from '../stores/ai-chat-panel.store'

interface UseAiChatContextReturn {
  chips: ComputedRef<ContextChip[]>
  rawChips: ComputedRef<ContextChip[]>
  clearChip: (id: string) => void
  unmuteAllChips: () => void
  setHoveredMetric: (id: string | null) => void
  setSelectedRec: (rec: { id?: string; title?: string; [k: string]: unknown } | null) => void
}

export function useAiChatContext(): UseAiChatContextReturn {
  const route = useRoute()
  const atlas = useAtlasIntelStore()
  const panel = useAiChatPanelStore()

  // ── Raw chips: derived only, no muting applied ────────────────────────────
  const rawChips = computed<ContextChip[]>(() => {
    const out: ContextChip[] = []

    if (route.path) {
      out.push({
        id: 'route',
        label: routeLabel(route.path),
        kind: 'route',
        removable: false,
        data: { path: route.path }
      })
    }

    for (const c of atlas.filterClusters ?? []) {
      out.push({
        id: `cluster:${c}`,
        label: `Cluster: ${c}`,
        kind: 'cluster',
        removable: true,
        data: { cluster: c }
      })
    }

    for (const pid of atlas.filterProjectIds ?? []) {
      const name = lookupProjectName(atlas, pid)
      out.push({
        id: `project:${pid}`,
        label: `Project: ${name}`,
        kind: 'project',
        removable: true,
        data: { projectId: pid, name }
      })
    }

    const dateLabel = formatDateRange(atlas, route)
    if (dateLabel) {
      out.push({
        id: 'dateRange',
        label: dateLabel,
        kind: 'dateRange',
        removable: true,
        data: {
          timeRange: atlas.timeRange,
          costPreset: atlas.costPreset,
          start: atlas.costMonthBounds?.start ?? null,
          end: atlas.costMonthBounds?.end ?? null
        }
      })
    }

    if (panel.hoveredMetric) {
      out.push({
        id: `metric:${panel.hoveredMetric}`,
        label: `Metric: ${panel.hoveredMetric}`,
        kind: 'metric',
        removable: true,
        data: { metricId: panel.hoveredMetric }
      })
    }

    if (panel.selectedRec) {
      const rec = panel.selectedRec
      const label = rec.title || rec.id || 'Recommendation'
      out.push({
        id: `rec:${rec.id ?? 'sel'}`,
        label: `Rec: ${label}`,
        kind: 'rec',
        removable: true,
        data: { rec }
      })
    }

    return out
  })

  // Apply muting from store and write through so `panel.contextChips` stays
  // in sync for any consumer that doesn't run this composable directly.
  const chips = computed<ContextChip[]>(() =>
    rawChips.value.filter((c) => !panel.mutedChipIds.has(c.id))
  )

  watch(
    rawChips,
    (next) => {
      panel.setChips(next)
    },
    { immediate: true, deep: false }
  )

  return {
    chips,
    rawChips,
    clearChip: panel.clearChip,
    unmuteAllChips: panel.unmuteAllChips,
    setHoveredMetric: panel.setHoveredMetric,
    setSelectedRec: panel.setSelectedRec
  }
}

// ── helpers ─────────────────────────────────────────────────────────────────
function routeLabel(path: string): string {
  const seg = path.split('/').filter(Boolean).pop() || 'home'
  // Title-case, swap dashes for spaces for readability
  return `Route: ${seg.replace(/-/g, ' ')}`
}

function lookupProjectName(atlas: ReturnType<typeof useAtlasIntelStore>, pid: string): string {
  const p = atlas.availableProjects?.find((x) => x.id === pid)
  return p?.name ?? pid
}

function formatDateRange(
  atlas: ReturnType<typeof useAtlasIntelStore>,
  route: ReturnType<typeof useRoute>
): string {
  // 1) Explicit ?from=..&to=.. wins
  const q = route.query ?? {}
  const from = typeof q.from === 'string' ? q.from : ''
  const to = typeof q.to === 'string' ? q.to : ''
  if (from && to) return `Range: ${from} → ${to}`

  // 2) Cost-page bounds when we're on the cost route
  const bounds = atlas.costMonthBounds
  if (bounds && (bounds.start || bounds.end)) {
    if (bounds.start && bounds.end && bounds.start === bounds.end) {
      return `Month: ${bounds.start.slice(0, 7)}`
    }
    return `Range: ${bounds.start ?? '…'} → ${bounds.end ?? 'now'}`
  }

  // 3) Global timeRange enum
  const tr = atlas.timeRange
  if (!tr) return ''
  const labels: Record<string, string> = {
    '7d': 'Last 7 days',
    '14d': 'Last 14 days',
    '30d': 'Last 30 days',
    '90d': 'Last 90 days',
    '3m': 'Last 3 months',
    '6m': 'Last 6 months',
    '12m': 'Last 12 months',
    all: 'All time'
  }
  return `Range: ${labels[tr] ?? tr}`
}
